import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import axios from 'axios';
import { 
  TrendingUp, 
  MoreHorizontal,
  X,
  Calendar,
  ArrowUpRight,
  CheckCircle2,
  ChevronRight
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Network logos
const MTN_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/9/93/New-mtn-logo.jpg/1200px-New-mtn-logo.jpg";
const ORANGE_LOGO = "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Orange_logo.svg/1200px-Orange_logo.svg.png";

// Signal bars component
const SignalBars = ({ strength = 4, color = '#16A34A' }) => (
  <div className="flex items-end gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <div 
        key={i}
        className="w-1 rounded-sm"
        style={{ 
          height: `${4 + i * 3}px`,
          backgroundColor: i <= strength ? color : '#E5E7EB'
        }}
      />
    ))}
  </div>
);

export const Dashboard = () => {
  const { user } = useAuth();
  const { formatWithConversion, formatSimple, exchangeRates } = useCurrency();
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rightPanelContainer, setRightPanelContainer] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    const container = document.getElementById('right-panel-container');
    if (container) {
      setRightPanelContainer(container);
    }
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, txRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
        axios.get(`${API}/transactions?limit=5`, { withCredentials: true })
      ]);
      setStats(statsRes.data);
      const txData = txRes.data;
      setTransactions(Array.isArray(txData) ? txData : (txData.transactions || []));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000 / 60);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  // Chart data for Transaction Volume
  const chartData = [
    { name: 'W1', value: 45000 },
    { name: 'W2', value: 65000 },
    { name: 'W3', value: 85000 },
    { name: 'W4', value: 55000 },
    { name: 'W5', value: 95000 },
    { name: 'W6', value: 75000 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Calculate total volume
  const totalVolumeLRD = stats?.today?.total_amount_lrd || 0;
  const totalVolumeUSD = stats?.today?.total_amount_usd || 0;
  const totalVolume = totalVolumeLRD + (totalVolumeUSD * 192);
  const displayVolume = totalVolume > 0 ? totalVolume : 109250;

  // Get confirmed and pending counts
  const confirmedCount = stats?.today?.confirmed || 7;
  const pendingCount = stats?.today?.pending || 3;

  // Sample transactions if none exist
  const sampleTransactions = [
    { transaction_id: '1', description: 'John Doe', amount: 500, currency: 'LRD', network: 'MTN', status: 'confirmed', created_at: new Date(Date.now() - 2*60000) },
    { transaction_id: '2', description: 'Mary Smith', amount: 20, currency: 'USD', network: 'Orange', status: 'pending', created_at: new Date(Date.now() - 3*60000) },
    { transaction_id: '3', description: 'Alex Cooper', amount: 1200, currency: 'LRD', network: 'MTN', status: 'confirmed', created_at: new Date(Date.now() - 6*60000) },
    { transaction_id: '4', description: 'Richard T', amount: 900, currency: 'LRD', network: 'Orange', status: 'pending', created_at: new Date(Date.now() - 11*60000) },
    { transaction_id: '5', description: 'Sarah K', amount: 50, currency: 'USD', network: 'MTN', status: 'failed', created_at: new Date(Date.now() - 21*60000) },
  ];
  
  const displayTransactions = transactions.length > 0 ? transactions.slice(0, 5) : sampleTransactions;

  // Right Panel Content
  const RightPanelContent = () => (
    <>
      {/* SIM Network Status - MTN */}
      <div 
        className="bg-white p-4 flex-shrink-0"
        style={{ 
          height: '150px',
          borderRadius: '12px',
          boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
        }}
        data-testid="sim-status-mtn"
      >
        <div className="flex items-start gap-3">
          <img src={MTN_LOGO} alt="MTN" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>MTN SIM</span>
              <span 
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
              >
                Active
              </span>
            </div>
            <p style={{ fontSize: '12px', color: '#16A34A', marginBottom: '6px' }}>Status: Active</p>
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Signal: <span style={{ color: '#111827', fontWeight: 500 }}>Strong</span>
              </span>
              <SignalBars strength={5} color="#16A34A" />
            </div>
            <p style={{ fontSize: '12px', color: '#9CA3AF' }}>Last SMS: 2 minutes ago</p>
          </div>
        </div>
      </div>

      {/* SIM Network Status - Orange */}
      <div 
        className="bg-white p-4 flex-shrink-0"
        style={{ 
          height: '150px',
          borderRadius: '12px',
          boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
        }}
        data-testid="sim-status-orange"
      >
        <div className="flex items-start gap-3">
          <img src={ORANGE_LOGO} alt="Orange" className="w-12 h-12 rounded-lg object-contain bg-white p-1 flex-shrink-0 border border-gray-100" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Orange SIM</span>
              <span 
                className="px-2 py-0.5 rounded text-xs font-medium"
                style={{ backgroundColor: '#DCFCE7', color: '#16A34A' }}
              >
                Active
              </span>
            </div>
            <p style={{ fontSize: '12px', color: '#16A34A', marginBottom: '6px' }}>Status: Active</p>
            <div className="flex items-center justify-between mb-1">
              <span style={{ fontSize: '12px', color: '#6B7280' }}>
                Signal: <span style={{ color: '#111827', fontWeight: 500 }}>Good</span>
              </span>
              <SignalBars strength={4} color="#16A34A" />
            </div>
            <p style={{ fontSize: '12px', color: '#9CA3AF' }}>Last SMS: 5 minutes ago</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div 
        className="bg-white p-4 flex-shrink-0"
        style={{ 
          height: '110px',
          borderRadius: '12px',
          boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
        }}
        data-testid="quick-actions"
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '12px' }}>Quick Actions</h3>
        <div className="grid grid-cols-3 gap-2">
          <Link 
            to="/app/transactions/new"
            className="py-2 px-2 rounded-lg text-center text-white text-xs font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: '#14B8A6' }}
          >
            Create Transaction
          </Link>
          <button 
            className="py-2 px-2 rounded-lg text-center text-white text-xs font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: '#14B8A6' }}
          >
            Generate QR
          </button>
          <button 
            className="py-2 px-2 rounded-lg text-center text-white text-xs font-medium transition-colors hover:opacity-90"
            style={{ backgroundColor: '#14B8A6' }}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Transaction Volume Chart */}
      <div 
        className="bg-white p-4 flex-1 flex flex-col hover-lift"
        style={{ 
          minHeight: '180px',
          borderRadius: '12px',
          boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
        }}
        data-testid="volume-chart"
      >
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#111827', marginBottom: '8px' }}>
          Transaction Volume <span style={{ color: '#9CA3AF', fontWeight: 400, fontSize: '12px' }}>(Last 30 Days)</span>
        </h3>
        <div className="flex-1" style={{ minHeight: '120px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={chartData} 
              barCategoryGap="25%"
              onClick={(data) => {
                if (data && data.activePayload && data.activePayload[0]) {
                  setSelectedWeek(data.activePayload[0].payload);
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              <XAxis 
                dataKey="name" 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
              />
              <YAxis 
                axisLine={false} 
                tickLine={false}
                tick={{ fill: '#9CA3AF', fontSize: 10 }}
                tickFormatter={(value) => `${value / 1000}k`}
                width={30}
              />
              <Tooltip 
                formatter={(value) => [`LRD ${value.toLocaleString()}`, 'Volume']}
                contentStyle={{ 
                  backgroundColor: '#111827', 
                  border: 'none', 
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '12px',
                  padding: '8px 12px'
                }}
                cursor={false}
              />
              <defs>
                <linearGradient id="volumeGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#38BDF8" />
                  <stop offset="100%" stopColor="#14B8A6" />
                </linearGradient>
              </defs>
              <Bar 
                dataKey="value" 
                fill="url(#volumeGradient)" 
                radius={[4, 4, 0, 0]}
                onMouseEnter={(data, index, e) => {
                  if (e && e.target) e.target.style.filter = 'brightness(1.1)';
                }}
                onMouseLeave={(data, index, e) => {
                  if (e && e.target) e.target.style.filter = 'brightness(1)';
                }}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Animation Styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
        .hover-lift {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-lift:hover {
          transform: translateY(-3px);
          box-shadow: 0 12px 24px -8px rgba(0, 0, 0, 0.12);
        }
        .stagger-1 { animation-delay: 50ms; }
        .stagger-2 { animation-delay: 100ms; }
        .stagger-3 { animation-delay: 150ms; }
      `}</style>

      {/* Week Details Modal */}
      {selectedWeek && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50"
          onClick={() => setSelectedWeek(null)}
        >
          <div 
            className="bg-white rounded-2xl p-6 w-full max-w-md animate-fadeIn border border-gray-200"
            style={{ boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-teal-50">
                  <Calendar className="w-5 h-5 text-teal-500" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {selectedWeek.name} Details
                  </h3>
                  <p className="text-xs text-gray-500">Weekly transaction summary</p>
                </div>
              </div>
              <button 
                onClick={() => setSelectedWeek(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-all duration-200 hover:scale-110"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpRight className="w-4 h-4 text-teal-500" />
                  <span className="text-xs text-gray-500">Total Volume</span>
                </div>
                <p className="text-lg font-bold text-gray-900">
                  {formatWithConversion(selectedWeek.value, 'LRD')}
                </p>
              </div>
              <div className="p-4 rounded-xl bg-gray-50">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="w-4 h-4 text-sky-500" />
                  <span className="text-xs text-gray-500">Transactions</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {Math.floor(selectedWeek.value / 15000) || 0}
                </p>
              </div>
            </div>

            {/* Action Button */}
            <Link 
              to="/app/transactions"
              className="flex items-center justify-center gap-2 w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-all duration-200 active:scale-95"
              onClick={() => setSelectedWeek(null)}
            >
              View All Transactions
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}

      {/* Main Dashboard Content */}
      <div 
        className="h-full flex flex-col overflow-hidden animate-fadeIn"
        style={{ gap: '16px' }}
        data-testid="merchant-dashboard"
      >
        {/* ROW 1 - Summary Cards */}
        <div 
          className="grid flex-shrink-0"
          style={{ gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}
        >
          {/* Today's Volume Card */}
          <div 
            className="bg-white p-5 flex flex-col justify-between hover-lift animate-fadeIn stagger-1"
            style={{ 
              height: '110px',
              borderRadius: '12px',
              boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
            }}
            data-testid="card-volume"
          >
            <div className="flex items-center justify-between">
              <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: 500 }}>Today's Volume</span>
              <TrendingUp className="w-5 h-5 transition-transform duration-300 hover:scale-125" style={{ color: '#16A34A' }} />
            </div>
            <div style={{ fontSize: '20px', fontWeight: 700, color: '#111827' }}>
              {formatWithConversion(displayVolume, 'LRD')}
            </div>
          </div>

          {/* Confirmed Card */}
          <div 
            className="bg-white p-5 flex flex-col justify-between hover-lift animate-fadeIn stagger-2"
            style={{ 
              height: '110px',
              borderRadius: '12px',
              boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
            }}
            data-testid="card-confirmed"
          >
            <div className="flex items-center justify-between">
              <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: 500 }}>Confirmed</span>
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-125"
                style={{ backgroundColor: '#DCFCE7' }}
              >
                <TrendingUp className="w-3 h-3" style={{ color: '#16A34A' }} />
              </div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#16A34A' }}>
              {confirmedCount}
            </div>
          </div>

          {/* Pending Card */}
          <div 
            className="bg-white p-5 flex flex-col justify-between hover-lift animate-fadeIn stagger-3"
            style={{ 
              height: '110px',
              borderRadius: '12px',
              boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
            }}
            data-testid="card-pending"
          >
            <div className="flex items-center justify-between">
              <span style={{ fontSize: '16px', color: '#6B7280', fontWeight: 500 }}>Pending</span>
              <div 
                className="w-6 h-6 rounded-full flex items-center justify-center transition-transform duration-300 hover:scale-125"
                style={{ backgroundColor: '#FEF3C7' }}
              >
                <TrendingUp className="w-3 h-3" style={{ color: '#F59E0B' }} />
              </div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: 700, color: '#F59E0B' }}>
              {pendingCount}
            </div>
          </div>
        </div>

        {/* ROW 2 - Data Table + Network Breakdown */}
        <div className="flex-1 flex flex-col overflow-hidden" style={{ gap: '16px' }}>
          {/* Recent Payments Table */}
          <div 
            className="bg-white flex flex-col overflow-hidden hover-lift"
            style={{ 
              height: '320px',
              borderRadius: '12px',
              boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
            }}
            data-testid="recent-payments"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
              <h2 style={{ fontSize: '16px', fontWeight: 600, color: '#111827' }}>Recent Payments</h2>
              <button className="text-gray-400 hover:text-gray-600 transition-transform duration-200 hover:scale-110">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left" style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>Customer</th>
                    <th className="px-5 py-3 text-left" style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>Amount</th>
                    <th className="px-5 py-3 text-left" style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>Network</th>
                    <th className="px-5 py-3 text-left" style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>Status</th>
                    <th className="px-5 py-3 text-left" style={{ fontSize: '12px', fontWeight: 500, color: '#9CA3AF' }}>Time</th>
                  </tr>
                </thead>
                <tbody>
                  {displayTransactions.map((tx) => (
                    <tr key={tx.transaction_id} className="border-b border-gray-50">
                      <td className="px-5 py-3">
                        <span style={{ fontSize: '14px', fontWeight: 500, color: '#111827' }}>
                          {tx.description || 'Customer'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#111827' }}>
                          {formatWithConversion(tx.amount, tx.currency || 'LRD')}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          {tx.network === 'MTN' ? (
                            <img src={MTN_LOGO} alt="MTN" className="w-6 h-6 rounded object-cover" />
                          ) : (
                            <img src={ORANGE_LOGO} alt="Orange" className="w-6 h-6 rounded object-contain" />
                          )}
                          <span style={{ fontSize: '14px', color: '#6B7280' }}>{tx.network}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <span 
                          className="inline-flex px-3 py-1 rounded-full"
                          style={{ 
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: tx.status === 'confirmed' ? '#DCFCE7' : tx.status === 'pending' ? '#FEF3C7' : '#FEE2E2',
                            color: tx.status === 'confirmed' ? '#16A34A' : tx.status === 'pending' ? '#D97706' : '#DC2626'
                          }}
                        >
                          {tx.status === 'confirmed' ? 'Confirmed' : tx.status === 'pending' ? 'Pending' : 'Failed'}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span style={{ fontSize: '14px', color: '#9CA3AF' }}>
                          {getTimeAgo(tx.created_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Network Breakdown - 2 cards side by side */}
          <div 
            className="grid flex-shrink-0"
            style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}
          >
            {/* MTN Card */}
            <div 
              className="bg-white p-5"
              style={{ 
                height: '140px',
                borderRadius: '12px',
                boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
              }}
              data-testid="network-mtn"
            >
              <div className="flex items-center gap-3 mb-3">
                <img src={MTN_LOGO} alt="MTN" className="w-10 h-10 rounded-lg object-cover" />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>MTN Mobile Money</p>
                  <p style={{ fontSize: '12px', color: '#9CA3AF' }}>Total Volume</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>LRD 57,200</p>
                <div 
                  className="w-12 h-2 rounded-full"
                  style={{ backgroundColor: '#FFCC00' }}
                ></div>
              </div>
            </div>

            {/* Orange Card */}
            <div 
              className="bg-white p-5"
              style={{ 
                height: '140px',
                borderRadius: '12px',
                boxShadow: '0px 6px 18px rgba(0,0,0,0.06)'
              }}
              data-testid="network-orange"
            >
              <div className="flex items-center gap-3 mb-3">
                <img src={ORANGE_LOGO} alt="Orange" className="w-10 h-10 rounded-lg object-contain bg-white p-1" />
                <div>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#111827' }}>Orange Money</p>
                  <p style={{ fontSize: '12px', color: '#9CA3AF' }}>Total Volume</p>
                </div>
              </div>
              <div className="flex items-end justify-between">
                <p style={{ fontSize: '24px', fontWeight: 700, color: '#111827' }}>LRD 21,250</p>
                <div 
                  className="w-12 h-2 rounded-full"
                  style={{ backgroundColor: '#FF7900' }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel via Portal */}
      {rightPanelContainer && createPortal(<RightPanelContent />, rightPanelContainer)}
    </>
  );
};

export default Dashboard;
